import { selectUserConfiguration } from '@/features/domain/user'
import { format, parse } from 'date-fns/esm'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useDateFnsOptions } from '../../useDateFnsOptions'
import { formatDate } from '../time/useFormatDate'
import { useTexts } from './useTexts'

function getStartTranslation(
  recurrence: uui.domain.client.rm.Recurrence,
  userConfig: uui.domain.UserConfiguration,
  locale: Locale,
  texts: ReturnType<typeof useTexts>,
) {
  const { recurrencePeriod } = recurrence

  switch (recurrencePeriod.recurrenceType) {
    case 'DAILY':
      return texts.description.daily

    case 'WEEKLY': {
      return texts.description.weekly(recurrencePeriod.period, recurrencePeriod.weekday)
    }

    case 'MONTHLY': {
      return recurrencePeriod.when === 'ON_DATE'
        ? texts.description.monthlyOnDate(
            recurrencePeriod.period,
            format(parse(recurrencePeriod.date, 'yyyyMMdd', new Date()), 'do', { locale }),
          )
        : texts.description.monthlyOnCardinal(
            recurrencePeriod.period,
            recurrencePeriod.cardinal,
            recurrencePeriod.weekday,
          )
    }

    case 'ANNUALLY': {
      return recurrencePeriod.when === 'ON_DATE'
        ? texts.description.annuallyOnDate(
            recurrencePeriod.period,
            formatDate(parse(recurrencePeriod.date, 'yyyyMMdd', new Date()), true, userConfig),
          )
        : texts.description.annuallyOnCardinal(
            recurrencePeriod.period,
            recurrencePeriod.cardinal,
            recurrencePeriod.weekday,
            recurrencePeriod.month,
          )
    }
  }
}

function getEndTranslation(
  recurrence: uui.domain.client.rm.Recurrence,
  userConfig: uui.domain.UserConfiguration,
  texts: ReturnType<typeof useTexts>,
) {
  switch (recurrence.recurrenceEndType) {
    case 'NEVER':
      return undefined

    case 'ON_DATE': {
      if (!recurrence.endDate) {
        throw new Error('Recurrence end date is required for ON_DATE type')
      }

      const endDate = parse(recurrence.endDate, 'yyyyMMdd', new Date())
      const formattedDate = formatDate(endDate, false, userConfig)
      return texts.description.endsOnDate(formattedDate)
    }

    case 'AFTER_NUMBER':
      if (typeof recurrence.requiredRecurrences !== 'number') {
        throw new Error('Recurrence required recurrences is required for AFTER_NUMBER type')
      }
      return texts.description.endsAfterNumber(recurrence.requiredRecurrences)

    default:
      throw new Error(`Unknown recurrence end type: ${recurrence.recurrenceEndType}`)
  }
}

export function useFormatRecurrence() {
  const userConfig = useSelector(selectUserConfiguration)
  const { locale } = useDateFnsOptions()
  const texts = useTexts()

  return useCallback(
    (recurrence: uui.domain.client.rm.Recurrence) => {
      const startTranslation = getStartTranslation(recurrence, userConfig, locale, texts)
      const endTranslation = getEndTranslation(recurrence, userConfig, texts)
      return endTranslation ? `${startTranslation}, ${endTranslation}` : startTranslation
    },
    [userConfig, locale, texts],
  )
}
